import React, { useMemo, useState } from "react";
import HistoryFilterBar from "./HistoryFilterBar";
import TradingViewChartIcon from './TradingViewChartIcon';
import "./CrossCombinationView.css";
import "./TrendCombinationView.css";
import "./TakeProfitView.css";
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

/** ============ Small Utilities ============ */
const norm = (v) => (typeof v === "string" ? v.toUpperCase() : "");
const isBullOrBear = (v) => {
  const n = norm(v);
  return n.includes("BULL") || n.includes("BEAR");
};
const isActiveCrossValue = (v, token = "") => {
  if (v === true || norm(v) === "TRUE") return !token;
  return isBullOrBear(v) && includesToken(v, token);
};
const includesToken = (v, token) => {
  const nv = norm(v);
  const tk = norm(token);
  return !tk || nv.includes(tk);
};
const getCrossClass = (v) => {
  const nv = norm(v);
  if (nv.includes("BULL")) return "bull";
  if (nv.includes("BEAR")) return "bear";
  return "";
};
const tfLabel = (v) => (Number.isFinite(+v) ? `${v} min` : v);

/** ===========================================================
 *  MAIN PAGE
 *  - Renders the top HistoryFilterBar
 *  - Calls one of the two backend history APIs
 *  - Shows three tabs (Cross / Trend / Take Profit)
 *  =========================================================== */
export default function SignalHistoryDashboard() {
  const { workspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState("cross"); // cross | trend | tp
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [globalCandleCloseTime, setGlobalCandleCloseTime] = useState("");

  const allCandleTimes = useMemo(() => {
  const values = ensureArray(rows).map((r) => r.calculatedCandleCloseTime).filter(Boolean);
  const unique = [...new Set(values)];
  return unique.sort((a, b) => (a < b ? 1 : -1)); // newest first
   }, [rows]);

  // This callback is passed to HistoryFilterBar
  const onFetchHistory = async (
    symbols,
    timeFrames,
    noOfPrevCandles,
    cutOffInHours
  ) => {
    setLoading(true);
    setError("");
    setRows([]);
    setGlobalCandleCloseTime("");
    try {
      if (noOfPrevCandles) {
        const url = buildScopedApiUrl(
          '/api/bot/ui/get/mtfCrossSignalHistoryBySymbolListAndTimeFrameList',
          workspace,
          {
            symbols,
            timeFrame: timeFrames,
            noOfPrevCandles,
          }
        );
        const res = await fetch(url, { headers: getApiHeaders() });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = await res.json();
        const safeRows = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn("Unexpected signal history candles payload shape", workspace, data);
        }
        setRows(safeRows);
      } else if (cutOffInHours) {
        const url = buildScopedApiUrl(
          '/api/bot/ui/get/mtfCrossSignalHistoryBySymbolListAndTimeFrameListAndCutOff',
          workspace,
          {
            symbol: symbols,
            timeFrames,
            cutOffInHours,
          }
        );
        const res = await fetch(url, { headers: getApiHeaders() });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = await res.json();
        const safeRows = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn("Unexpected signal history cutoff payload shape", workspace, data);
        }
        setRows(safeRows);
      } else {
        throw new Error(
          "Please provide either No. of Previous Candles or Cut-off in Hours."
        );
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to fetch history.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Top Filter Bar */}
      <HistoryFilterBar onFetchHistory={onFetchHistory} />

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: 8,
          margin: "0 20px 12px",
        }}
      >
        <button
          onClick={() => setActiveTab("cross")}
          style={tabStyle(activeTab === "cross")}
        >
          Cross Signals
        </button>
        <button
          onClick={() => setActiveTab("trend")}
          style={tabStyle(activeTab === "trend")}
        >
          Trend Dashboard
        </button>
        <button
          onClick={() => setActiveTab("tp")}
          style={tabStyle(activeTab === "tp")}
        >
          Additional TP Signals
        </button>

        {/* Global Candle Close Time Filter (applies to all tabs) */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 20px 12px" }}>
          <label style={{ color: "#0f172a", fontWeight: 600 }}>Candle Time:</label>
          <select
            value={globalCandleCloseTime}
            onChange={(e) => setGlobalCandleCloseTime(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #ccc",
              backgroundColor: "#f1f5f9",
              color: "black",
              minWidth: 260,
            }}
            disabled={rows.length === 0}
          >
            <option value="">All Times</option>
            {allCandleTimes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          {globalCandleCloseTime && (
            <button
              onClick={() => setGlobalCandleCloseTime("")}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: "1px solid #475569",
                background: "#1e293b",
                color: "white",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              Clear
            </button>
          )}
        </div>

      </div>

      {/* State */}
      {loading && (
        <div style={stateBoxStyle}>Fetching historical signals…</div>
      )}
      {error && <div style={errorBoxStyle}>{error}</div>}
      {!loading && !error && rows.length === 0 && (
        <div style={stateBoxStyle}>No results yet. Run a search above.</div>
      )}

      {/* Views */}
      {!loading && !error && rows.length > 0 && (
        <>
        {activeTab === "cross" && (
          <CrossCombinationViewHistory
            data={rows}
            globalCandleCloseTime={globalCandleCloseTime}
          />
        )}
        {activeTab === "trend" && (
          <TrendCombinationViewHistory
            data={rows}
            globalCandleCloseTime={globalCandleCloseTime}
          />
        )}
        {activeTab === "tp" && (
          <TakeProfitViewHistory
            data={rows}
            globalCandleCloseTime={globalCandleCloseTime}
          />
        )}
        </>
      )}
    </div>
  );
}

/** Button styles for tabs */
function tabStyle(active) {
  return {
    padding: "8px 12px",
    borderRadius: 6,
    border: "1px solid #475569",
    background: active ? "#0ea5e9" : "#1e293b",
    color: "white",
    cursor: "pointer",
    fontWeight: "bold",
  };
}

const stateBoxStyle = {
  margin: "12px 20px",
  background: "#1e293b",
  color: "#e5e7eb",
  padding: "12px",
  borderRadius: 8,
  border: "1px solid #334155",
};
const errorBoxStyle = {
  ...stateBoxStyle,
  border: "1px solid #b91c1c",
  background: "#2a1616",
};

/** ===========================================================
 *  CROSS (History)
 *  - Adds calculatedCandleCloseTime column
 *  - Same look/feel as your CrossCombinationView
 *  =========================================================== */
const crossFields = [
  'volumeBreakout',
  'ema200TrendAlignedEma50BandCross',
  'appPriceXEma50BandCross',
  'priceInsideEma50Band',
  'ema50BandChoppyRangeBreakOut',
  'ema50BandChoppyRangeBreakOutTrendAligned',
  'priceInsideEma200Band',
  'ema200BandCross',
  'ema200BandCrossTrendAligned',
  'firstEma50BandCrossAfterTrendShift',
  'firstEma50BandCrossInLast50Candles',
  'firstEma50BandCrossInLast100Candles',
  'firstEma50BandCrossInLast200Candles',
  'firstEma200BandCrossAfterTrendShift',
  'firstEma200BandCrossInLast50Candles',
  'firstEma200BandCrossInLast100Candles',
  'firstEma200BandCrossInLast200Candles',
  'firstTrendAlignedStochRsiResetAfterTrendShift',
  'firstTrendAlignedStochRsiResetAtEma50BandAfterTrendShift',
  'firstTrendAlignedStochRsiResetAtEma200BandAfterTrendShift',
  "singleGodCandleCrossedAllEmaLevels",
  "singleCandleCrossedLowerEmaStack",
  "priceBeyondAllEmaStackAndEma200Cross",
  "priceXAllEmaStackEdgeCross",
  "priceXLowerEmaStackEdgeCross",
  "priceAboveOrBelowAllEmaLevels",
  "allEmaLevelsInOneTrend",
  "ictMSB",
  "ictSwingHighLowRetest",
  "ictSwingHighLowWickRetest",
  "ictSupplyDemandReversal",
  "ictBreakerReversal",
  "candleTrendReversal",

  "ictManualTradeEntryExitSrCross",
  "appMtfEmaConfirmedCross",
  "appPriceXPosCloseSrConfirmedCross",
  "appPriceXSrConfirmedCross",
  "appPriceXZoneConfirmedCross",
  "priceXSrConfirmedCross",
  "dualFlexiEmaEntryExit",
  "trendBasedStochRsiEntrySignal",
  "trendBasedExtremeStochRsiEntrySignal",
  "ignoreEmaSignalRangeSrCross",
  "szDzZoneBasedStochRsiEntrySignal",
  "appRsiDivEntrySignal",
  "appAoDivEntrySignal",
  "appTouchCloseSrCrossSignal",
  "fvgTagged",
  "appPriceXEma50Cross",
  "appPriceXEma100Cross",
  "appPriceXEma200Cross",
  "appEma50XEma200Cross",
  "mtfPriceXEma100Ema200Cross",
  "mtfPriceXEma100Ema200TrendOnlyCross",
  "ema12ema21SwingHighCloseSrCrossSignal",
  "ema12ema21SwingLowCloseSrCrossSignal",
  "ema12ema21SwingHighWickSrCrossSignal",
  "ema12ema21SwingLowWickSrCrossSignal",
  "ema12ema21SwingLevelStochRsiEntrySignal",
  "appPriceXEma12Cross",
  "appPriceXEma12Ema21Cross",
  "appEma12XEma21Cross",
  "appPriceXEma21Cross",
  "appPriceXEma36Cross",
  "mtfPriceXEma21Ema36Cross",
  "appEma21XEma36Cross",
  "mtfPriceXEma21Ema36TrendOnlyCross",
  "appEma21XEma36CrossEntrySRCrossSignal",
  "swingLevelStochRsiEntrySignal",
  "appPriceXVwap24Cross",
  "appPriceXVwapWeeklyCross",
  "priceXEma36Rejection",
  "candlePattern",
  "priceXSRTrendLineCross",
  "priceXChannelLineCross",
  "priceXSupplyDemandZoneCross",
  "priceXVWAP24Cross",
  "priceXVWAPWeeklyCross",
  "priceXEma12Cross",
  "priceXEma36Cross",
  "ema12XEma36Cross",
];

const crossTitles = (field) =>
  ({
    volumeBreakout: 'Volume Breakout',
    ema200TrendAlignedEma50BandCross: 'Ema50 Band Cross [EMA200 Trend Aligned]',
    appPriceXEma50BandCross: 'Ema50 Band Cross Signal',
    priceInsideEma50Band: 'Price Inside Ema50 Band',
    ema50BandChoppyRangeBreakOut: 'Ema50 Band Choppy Range BreakOut',
    ema50BandChoppyRangeBreakOutTrendAligned: 'Ema50 Band Choppy Range BreakOut Trend Aligned',
    priceInsideEma200Band: 'Price Inside Ema200 Band',
    ema200BandCross: 'Ema200 Band Cross',
    ema200BandCrossTrendAligned: 'Ema200 Band Cross Trend Aligned',
    firstEma50BandCrossAfterTrendShift: 'First Ema50 Band Cross After Trend Shift',
    firstEma50BandCrossInLast50Candles: 'First Ema50 Band Cross In Last 50 Candles',
    firstEma50BandCrossInLast100Candles: 'First Ema50 Band Cross In Last 100 Candles',
    firstEma50BandCrossInLast200Candles: 'First Ema50 Band Cross In Last 200 Candles',
    firstEma200BandCrossAfterTrendShift: 'First Ema200 Band Cross After Trend Shift',
    firstEma200BandCrossInLast50Candles: 'First Ema200 Band Cross In Last 50 Candles',
    firstEma200BandCrossInLast100Candles: 'First Ema200 Band Cross In Last 100 Candles',
    firstEma200BandCrossInLast200Candles: 'First Ema200 Band Cross In Last 200 Candles',
    firstTrendAlignedStochRsiResetAfterTrendShift: 'First Trend Aligned Stoch RSI Reset After Trend Shift',
    firstTrendAlignedStochRsiResetAtEma50BandAfterTrendShift: 'First Stoch RSI Reset At Ema50 Band After Trend Shift',
    firstTrendAlignedStochRsiResetAtEma200BandAfterTrendShift: 'First Stoch RSI Reset At Ema200 Band After Trend Shift',
    singleGodCandleCrossedAllEmaLevels: "Single God Candle Crossed All Ema Levels",
    singleCandleCrossedLowerEmaStack: "Single Candle Crossed Lower Ema Stack",
    priceBeyondAllEmaStackAndEma200Cross: "Price Beyond All EmaStack & EMA200",
    priceXAllEmaStackEdgeCross: "Price X All Ema Stack Edge Cross",
    priceXLowerEmaStackEdgeCross: "Price X Lower Ema Stack Edge Cross",
    priceAboveOrBelowAllEmaLevels: "Price Above/Below All Ema Levels",
    allEmaLevelsInOneTrend: "All Ema Levels In One Trend",
    ictMSB: "ICT MSB",
    ictSwingHighLowRetest: "ICT Swing High/Low Retest",
    ictSwingHighLowWickRetest: "ICT Swing High/Low Wick Retest",
    ictSupplyDemandReversal: "ICT Supply-Demand Reversal",
    ictBreakerReversal: "ICT Breaker Reversal",
    candleTrendReversal: "Candle Trend Reversal",
    ictManualTradeEntryExitSrCross: "ICT Manual Trade Entry/Exit SR Cross",
    appMtfEmaConfirmedCross: "App MTF EMA Confirmed Cross",
    appPriceXPosCloseSrConfirmedCross: "App Price X Pos Close SR Confirmed Cross",
    appPriceXSrConfirmedCross: "App Price X SR Confirmed Cross",
    appPriceXZoneConfirmedCross: "App Price X Zone Confirmed Cross",
    priceXSrConfirmedCross: "Price X S/R Confirmed Cross",
    dualFlexiEmaEntryExit: "Dual Flexi EMA Entry Exit",
    trendBasedStochRsiEntrySignal: "Trend Based StochRSI Entry",
    trendBasedExtremeStochRsiEntrySignal: "Trend Based Extreme StochRSI Entry",
    ignoreEmaSignalRangeSrCross: "Ignore EMA Signal Range SR Cross",
    szDzZoneBasedStochRsiEntrySignal: "SZ/DZ Zone Based StochRSI Entry",
    appRsiDivEntrySignal: "App RSI Div Entry",
    appAoDivEntrySignal: "App AO Div Entry",
    appTouchCloseSrCrossSignal: "App Touch Close SR Cross",
    fvgTagged: "FVG Tagged / Swept",
    appPriceXEma50Cross: "Price X EMA50 Cross",
    appPriceXEma100Cross: "Price X EMA100 Cross",
    appPriceXEma200Cross: "Price X EMA200 Cross",
    appEma50XEma200Cross: "EMA50 X EMA200 Cross",
    mtfPriceXEma100Ema200Cross: "MTF Price X EMA100/200 Cross",
    mtfPriceXEma100Ema200TrendOnlyCross: "MTF Price X EMA100/200 TrendOnly",
    ema12ema21SwingHighCloseSrCrossSignal: "EMA12/21 Swing High Close SR Cross",
    ema12ema21SwingLowCloseSrCrossSignal: "EMA12/21 Swing Low Close SR Cross",
    ema12ema21SwingHighWickSrCrossSignal: "EMA12/21 Swing High Wick SR Cross",
    ema12ema21SwingLowWickSrCrossSignal: "EMA12/21 Swing Low Wick SR Cross",
    ema12ema21SwingLevelStochRsiEntrySignal: "EMA12/21 Swing Level StochRSI Entry",
    appPriceXEma12Cross: "Price X EMA12 Cross",
    appPriceXEma12Ema21Cross: "Price X EMA12/EMA21 Cross",
    appEma12XEma21Cross: "EMA12 X EMA21 Cross",
    appPriceXEma21Cross: "Price X EMA21 Cross",
    appPriceXEma36Cross: "Price X EMA36 Cross",
    mtfPriceXEma21Ema36Cross: "MTF Price X EMA21/EMA36 Cross",
    appEma21XEma36Cross: "EMA21 X EMA36 Cross",
    mtfPriceXEma21Ema36TrendOnlyCross: "MTF Price X EMA21/EMA36 TrendOnly",
    appEma21XEma36CrossEntrySRCrossSignal: "EMA21 X EMA36 Entry SR Cross",
    swingLevelStochRsiEntrySignal: "Swing Level StochRSI Entry",
    appPriceXVwap24Cross: "Price X VWAP 24H Cross",
    appPriceXVwapWeeklyCross: "Price X VWAP Weekly Cross",
    priceXEma36Rejection: "Price X EMA36 Rejection/Bounce",
    candlePattern: "Engulfing Candle Pattern",
    priceXSRTrendLineCross: "Price X SR Trendline Cross",
    priceXChannelLineCross: "Price X Channel Line Cross",
    priceXSupplyDemandZoneCross: "Price X Supply/Demand Zone Cross",
    priceXVWAP24Cross: "Price X VWAP 24H Cross",
    priceXVWAPWeeklyCross: "Price X VWAP Weekly Cross",
    priceXEma12Cross: "Price X EMA12 Cross",
    priceXEma36Cross: "Price X EMA36 Cross",
    ema12XEma36Cross: "EMA12 X EMA36 Cross",
  }[field] || field);

function CrossCombinationViewHistory({ data, globalCandleCloseTime }) {
  const safeData = ensureArray(data);
  const [globalCrossType, setGlobalCrossType] = useState("");
  const [globalSymbol, setGlobalSymbol] = useState("");
  const [globalTimeFrame, setGlobalTimeFrame] = useState("");

  const filteredSymbols = useMemo(() => {
    return [
      ...new Set(
        safeData
          .filter((item) =>
            crossFields.some(
                (f) =>
                  isActiveCrossValue(item[f], globalCrossType)
              )
          )
          .map((d) => d.symbol)
      ),
    ];
  }, [safeData, globalCrossType]);

  const allTimeFrames = [...new Set(safeData.map((d) => d.timeFrame))].sort(
    (a, b) => a - b
  );

  const allCandleTimes = useMemo(() => {
  const values = safeData
    .map((d) => d.calculatedCandleCloseTime)
    .filter(Boolean);

  const unique = [...new Set(values)];

  // sort DESC newest first
  return unique.sort((a, b) => (a < b ? 1 : -1));
}, [safeData]);


  const clearGlobal = () => {
    setGlobalCrossType("");
    setGlobalSymbol("");
    setGlobalTimeFrame("");
  };

  return (
    <>
      {/* ───────────────────────────────────────────── */}
      {/* GLOBAL FILTER BAR */}
      {/* ───────────────────────────────────────────── */}
      <div
        className="cross-toolbar"
        style={{ display: "flex", alignItems: "center", gap: 15 }}
      >
        {/* Cross Type */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <select
            value={globalCrossType}
            onChange={(e) => setGlobalCrossType(e.target.value)}
          >
            <option value="">Select Cross Type</option>
            <option value="BULL">All BULL</option>
            <option value="BEAR">All BEAR</option>
          </select>
        </div>

        {/* Symbol */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <select
            value={globalSymbol}
            onChange={(e) => setGlobalSymbol(e.target.value)}
          >
            <option value="">Select Symbol</option>
            {filteredSymbols.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Time Frame */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <select
            value={globalTimeFrame}
            onChange={(e) => setGlobalTimeFrame(e.target.value)}
          >
            <option value="">All</option>
            {allTimeFrames.map((tf) => (
              <option key={tf} value={tf}>
                {tf} min
              </option>
            ))}
          </select>
        </div>

        {/* Clear */}
        <button className="clear-global-btn" onClick={clearGlobal}>
          Clear Filters
        </button>
      </div>
      {/* END GLOBAL BAR */}

      {/* SECTION RENDERING */}
      <div className="cross-container">
        {crossFields.map((field) => {
          const fieldData = safeData.filter((item) => {
            const value = item[field];
            if (!isActiveCrossValue(value, globalCrossType)) return false;

            // Apply all global filters here
            if (globalSymbol && item.symbol !== globalSymbol) return false;
            if (
              globalTimeFrame &&
              String(item.timeFrame) !== String(globalTimeFrame)
            )
              return false;
            if (
            globalCandleCloseTime &&
            item.calculatedCandleCloseTime !== globalCandleCloseTime
            )
            return false;


            return true;
          });

          if (!fieldData.length) return null;

          return (
            <CrossHistorySection key={field} field={field} data={fieldData} />
          );
        })}
      </div>
    </>
  );
}


function CrossHistorySection({ field, data }) {
  const safeData = ensureArray(data);
  const [showAll, setShowAll] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "calculatedCandleCloseTime", direction: "desc" });
  const [filters, setFilters] = useState({ symbol: "", timeFrame: "", crossType: "" });

  const symbols = [...new Set(safeData.map((d) => d.symbol))];
  const timeFrames = [...new Set(safeData.map((d) => d.timeFrame))].sort((a, b) => a - b);

  const filtered = safeData
    .filter((d) => !filters.symbol || d.symbol === filters.symbol)
    .filter((d) => !filters.timeFrame || d.timeFrame === +filters.timeFrame)
    .filter((d) => (!filters.crossType ? true : includesToken(d[field], filters.crossType)));

  const sorted = [...filtered].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;
    let va = a[key];
    let vb = b[key];

    if (key === "change") {
      const na = parseFloat(String(va || "").replace("%", "")) || 0;
      const nb = parseFloat(String(vb || "").replace("%", "")) || 0;
      return direction === "asc" ? na - nb : nb - na;
    }

    if (key === "volumeRank") {
      const na = parseInt(String(va || "")) || 0;
      const nb = parseInt(String(vb || "")) || 0;
      return direction === "asc" ? na - nb : nb - na;
    }

    if (typeof va === "number" && typeof vb === "number") {
      return direction === "asc" ? va - vb : vb - va;
    }
    return direction === "asc"
      ? String(va || "").localeCompare(String(vb || ""))
      : String(vb || "").localeCompare(String(va || ""));
  });

  const displayed = showAll ? sorted : sorted.slice(0, 10);

  const onSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };


    const getSignalSLValue = (item, field) => {
  const v = norm(item[field]);

  if (!v) return '';

  if (v.includes('SIGNAL_CLOSE')) return '';

  if (v.includes('BULL')) return item.bullishSignalSL || '';

  if (v.includes('BEAR')) return item.bearishSignalSL || '';

  return '';
};

  return (
    <div className="cross-section">
      <h3>{crossTitles(field)}</h3>

      <div className="filters">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}></div>
        <select
          value={filters.symbol}
          onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
        >
          <option value="">Symbol</option>
          {symbols.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        

        <select
          value={filters.timeFrame}
          onChange={(e) => setFilters({ ...filters, timeFrame: e.target.value })}
        >
          <option value="">Time Frame</option>
          {timeFrames.map((t) => (
            <option key={t} value={t}>
              {tfLabel(t)}
            </option>
          ))}
        </select>

        <select
          value={filters.crossType}
          onChange={(e) => setFilters({ ...filters, crossType: e.target.value })}
        >
          <option value="">Cross Type</option>
          <option value="BULL">BULL</option>
          <option value="BEAR">BEAR</option>
        </select>

        {filtered.length > 10 && (
          <button className="expand-btn" onClick={() => setShowAll((s) => !s)}>
            {showAll ? "Collapse" : "Expand"}
          </button>
        )}

        <button onClick={() => setFilters({ symbol: "", timeFrame: "", crossType: "" })}>
          Clear Filters
        </button>
      </div>

      <div className="cross-table-wrapper">
        <table className="cross-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => onSort("symbol")}>Symbol</th>
              <th>Chart</th>
              <th onClick={() => onSort("timeFrame")}>Time Frame</th>
              <th onClick={() => onSort("calculatedCandleCloseTime")}>
                Candle Close Time
              </th>
              <th onClick={() => onSort("marketPrice")}>Market Price</th>
              <th onClick={() => onSort("change")}>Change %</th>
              <th onClick={() => onSort('volumeRank')}>VolumeRank </th>
              <th>Signal SL</th>              
              <th onClick={() => onSort(field)}>{crossTitles(field)}</th>
              {field === "priceXLowerEmaStackEdgeCross" && (
                  <>
                    <th onClick={() => onSort('lowerEmaStackEdgeCrossSignalStopLoss')}>
                      SL 
                    </th>
                    <th onClick={() => onSort('lowerEmaStackEdgeCrossSignalStopLossDistance')}>
                      SL Dist % 
                    </th>
                  </>
                )}

            </tr>
          </thead>
          <tbody>
            {displayed.map((row, idx) => (
              <tr key={idx} className={getCrossClass(row[field])}>
                 <td>{idx + 1}</td>
                <td>{row.symbol}</td>
                                <td>
                                  <TradingViewChartIcon
                                    symbol={row.symbol}
                                    timeFrame={row.timeFrame}
                                    exchange={row.exchange}
                                  />
                                </td>
                <td>{tfLabel(row.timeFrame)}</td>
                <td>{row.calculatedCandleCloseTime || "-"}</td>
                <td>{row.marketPrice}</td>
                <td>{row.change}</td>
                <td>{row.volumeRank}</td>    
                <td>{getSignalSLValue(row, field)}</td> 
                <td>{row[field]}</td>
                {field === "priceXLowerEmaStackEdgeCross" && (
                  <>
                    <td>{row.lowerEmaStackEdgeCrossSignalStopLoss}</td>
                    <td>{row.lowerEmaStackEdgeCrossSignalStopLossDistance}</td>
                  </>
                )}                
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** ===========================================================
 *  TREND (History) — adds calculatedCandleCloseTime column
 *  =========================================================== */
const trendFields = [
  "priceXEma12Trend",
  "priceXEma21Trend",
  "priceXEma36Trend",
  "appEma21XEma36Trend",
  "priceXEma50Trend",
  "priceXEma100Trend",
  "priceXEma200Trend",
  "appEma50XEma200Trend",
  "appEma100XEma200Trend",
  "appEma50XEma100Trend",
  "priceXVWAP24Trend",
  "priceXVWAPWeeklyTrend",
];

function TrendCombinationViewHistory({ data, globalCandleCloseTime }) {
  const safeData = ensureArray(data);
  const [filters, setFilters] = useState({
    symbol: "",
    timeFrame: "",
    trendType: "",
  });
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: "calculatedCandleCloseTime",
    direction: "desc",
  });
  const [expanded, setExpanded] = useState(false);

  const symbols = [...new Set(safeData.map((d) => d.symbol))];
  const timeFrames = [...new Set(safeData.map((d) => d.timeFrame))].sort((a, b) => a - b);

  const filtered = useMemo(() => {
    return safeData.filter((item) => {
      if (globalCandleCloseTime && item.calculatedCandleCloseTime !== globalCandleCloseTime) return false;
      if (filters.symbol && item.symbol !== filters.symbol) return false;
      if (filters.timeFrame && item.timeFrame !== +filters.timeFrame) return false;

      if (filters.trendType === "ALL_BULL") {
        const nonEmpty = trendFields.filter((f) => item[f]);
        if (nonEmpty.length === 0 || !nonEmpty.every((f) => item[f] === "BULL"))
          return false;
      } else if (filters.trendType === "ALL_BEAR") {
        const nonEmpty = trendFields.filter((f) => item[f]);
        if (nonEmpty.length === 0 || !nonEmpty.every((f) => item[f] === "BEAR"))
          return false;
      }

      for (let f of trendFields) {
        if (columnFilters[f] && item[f] !== columnFilters[f]) return false;
      }
      return true;
    });
  }, [safeData, filters, columnFilters, globalCandleCloseTime]);

  const sorted = [...filtered].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;
    let va = a[key];
    let vb = b[key];

    if (key === "change") {
      const na = parseFloat(String(va || "").replace("%", "")) || 0;
      const nb = parseFloat(String(vb || "").replace("%", "")) || 0;
      return direction === "asc" ? na - nb : nb - na;
    }

    if (key === "volumeRank") {
      const na = parseInt(String(va || "")) || 0;
      const nb = parseInt(String(vb || "")) || 0;
      return direction === "asc" ? na - nb : nb - na;
    }

    if (typeof va === "number" && typeof vb === "number") {
      return direction === "asc" ? va - vb : vb - va;
    }
    return direction === "asc"
      ? String(va || "").localeCompare(String(vb || ""))
      : String(vb || "").localeCompare(String(va || ""));
  });

  const shown = expanded ? sorted : sorted.slice(0, 20);

  const onSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const getTrendClass = (v) =>
    v === "BULL" ? "trend-bull" : v === "BEAR" ? "trend-bear" : "";

  return (
    <div className="trend-container">
      <h3>Trend Combination View (History)</h3>

      <div className="filters">
        <select
          value={filters.symbol}
          onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
        >
          <option value="">Symbol</option>
          {symbols.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={filters.timeFrame}
          onChange={(e) => setFilters({ ...filters, timeFrame: e.target.value })}
        >
          <option value="">Time Frame</option>
          {timeFrames.map((t) => (
            <option key={t} value={t}>
              {tfLabel(t)}
            </option>
          ))}
        </select>

        <select
          value={filters.trendType}
          onChange={(e) => setFilters({ ...filters, trendType: e.target.value })}
        >
          <option value="">Select Trend Type</option>
          <option value="ALL_BULL">All BULL</option>
          <option value="ALL_BEAR">All BEAR</option>
        </select>

        {filtered.length > 20 && (
          <button className="expand-button" onClick={() => setExpanded((s) => !s)}>
            {expanded ? "Collapse" : "Expand"}
          </button>
        )}

        <button
          onClick={() => {
            setFilters({ symbol: "", timeFrame: "", trendType: "" });
            setColumnFilters({});
          }}
        >
          Clear Filters
        </button>
      </div>

      <div className="trend-table-wrapper">
        <table className="trend-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => onSort("symbol")}>Symbol</th>
               <th>Chart</th>
              <th onClick={() => onSort("timeFrame")}>Time Frame</th>
              <th onClick={() => onSort("calculatedCandleCloseTime")}>
                Candle Close Time
              </th>
              <th onClick={() => onSort('volumeRank')}>VolumeRank </th>
              {trendFields.map((f) => (
                <th key={f} onClick={() => onSort(f)}>
                  {f} {sortConfig.key === f ? (sortConfig.direction === "asc" ? "↑" : "↓") : "⬍"}
                </th>
              ))}
            </tr>
            <tr>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              {trendFields.map((f) => (
                <th key={`filter-${f}`}>
                  <select
                    value={columnFilters[f] || ""}
                    onChange={(e) =>
                      setColumnFilters({ ...columnFilters, [f]: e.target.value })
                    }
                  >
                    <option value="">All</option>
                    <option value="BULL">BULL</option>
                    <option value="BEAR">BEAR</option>
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((item, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>{item.symbol}</td>
                <td>
                  <TradingViewChartIcon
                    symbol={item.symbol}
                    timeFrame={item.timeFrame}
                    exchange={item.exchange}
                  />
                </td>                
                <td>{tfLabel(item.timeFrame)}</td>
                <td>{item.calculatedCandleCloseTime || "-"}</td>
                <td>{item.volumeRank}</td>                    
                {trendFields.map((f) => (
                  <td key={f} className={getTrendClass(item[f])}>
                    {item[f] || "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** ===========================================================
 *  TAKE PROFIT (History) — adds calculatedCandleCloseTime column
 *  =========================================================== */
const tpFields = [
  "sRsiAndRsiComboReversal",
  "swingHighCloseSrCrossSignal",
  "swingLowCloseSrCrossSignal",
  "swingHighWickSrCrossSignal",
  "swingLowWickSrCrossSignal",
  "swingLowTakenOut",
  "swingHighTakenOut",
  "swingLowWickTakenOut",
  "swingHighWickTakenOut",
  "ccr",
  "rsiReverse",
  "stochRsiCross",
  "rsiDiv",
  "aoDiv",
  "candlePattern",
];

const tpTitles = (f) =>
  ({
    sRsiAndRsiComboReversal: "Stoch RSI + RSI Combo Reversal",
    swingHighCloseSrCrossSignal: "Swing High Close SR Cross",
    swingLowCloseSrCrossSignal: "Swing Low Close SR Cross",
    swingHighWickSrCrossSignal: "Swing High Wick SR Cross",
    swingLowWickSrCrossSignal: "Swing Low Wick SR Cross",
    swingLowTakenOut: "Swing Low Taken Out",
    swingHighTakenOut: "Swing High Taken Out",
    swingLowWickTakenOut: "Swing Low Wick Liq Swept",
    swingHighWickTakenOut: "Swing High Wick Liq Swept",
    ccr: "CCR Signals",
    rsiReverse: "RSI Reversals",
    stochRsiCross: "Stoch RSI Cross",
    rsiDiv: "RSI Div",
    aoDiv: "AO Div",
    candlePattern: "Engulfing",
  }[f] || f);

function TakeProfitViewHistory({ data, globalCandleCloseTime }) {
  const safeData = ensureArray(data);
  const [globalSymbol, setGlobalSymbol] = useState("");
  const [globalSignalType, setGlobalSignalType] = useState("");

  const allSymbols = [...new Set(safeData.map((d) => d.symbol))];

  const hasBull = safeData.some((d) =>
    tpFields.some((f) => norm(d[f]).includes("BULL"))
  );
  const hasBear = safeData.some((d) =>
    tpFields.some((f) => norm(d[f]).includes("BEAR"))
  );

  const clear = () => {
    setGlobalSymbol("");
    setGlobalSignalType("");
  };

  return (
    <>
      <div className="tp-global-toolbar">
        <select
          value={globalSymbol}
          onChange={(e) => setGlobalSymbol(e.target.value)}
        >
          <option value="">Select Symbol</option>
          {allSymbols.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={globalSignalType}
          onChange={(e) => setGlobalSignalType(e.target.value)}
        >
          <option value="">Select Signal Type</option>
          {hasBull && <option value="BULL">All BULL</option>}
          {hasBear && <option value="BEAR">All BEAR</option>}
        </select>

        <button className="clear-global-btn" onClick={clear}>
          Clear Filters
        </button>
      </div>

      <div className="tp-container">
        {tpFields.map((field) => {
          const fieldData = safeData.filter((d) => d[field]);
          const filtered = fieldData.filter(
            (d) =>
              (!globalSymbol || d.symbol === globalSymbol) &&
              (!globalSignalType || norm(d[field]).includes(globalSignalType)) &&
      (!globalCandleCloseTime || d.calculatedCandleCloseTime === globalCandleCloseTime)
          );
          if (filtered.length === 0) return null;
          return <TPHistorySection key={field} field={field} data={filtered} />;
        })}
      </div>
    </>
  );
}

function TPHistorySection({ field, data }) {
  const safeData = ensureArray(data);
  const [showAll, setShowAll] = useState(false);
  const [filters, setFilters] = useState({
    symbol: "",
    timeFrame: "",
    signalType: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "calculatedCandleCloseTime",
    direction: "desc",
  });

  const symbols = [...new Set(safeData.map((d) => d.symbol))];
  const timeFrames = [...new Set(safeData.map((d) => d.timeFrame))].sort((a, b) => a - b);
  const uniqueSignals = [...new Set(safeData.map((d) => d[field]).filter(Boolean))];

  const filtered = safeData
    .filter((d) => !filters.symbol || d.symbol === filters.symbol)
    .filter((d) => !filters.timeFrame || d.timeFrame === +filters.timeFrame)
    .filter((d) => !filters.signalType || d[field] === filters.signalType);

  const sorted = [...filtered].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;
    let va = a[key];
    let vb = b[key];

    if (key === "change") {
      const na = parseFloat(String(va || "").replace("%", "")) || 0;
      const nb = parseFloat(String(vb || "").replace("%", "")) || 0;
      return direction === "asc" ? na - nb : nb - na;
    }

    if (key === "volumeRank") {
      const na = parseInt(String(va || "")) || 0;
      const nb = parseInt(String(vb || "")) || 0;
      return direction === "asc" ? na - nb : nb - na;
    }

    if (typeof va === "number" && typeof vb === "number") {
      return direction === "asc" ? va - vb : vb - va;
    }
    return direction === "asc"
      ? String(va || "").localeCompare(String(vb || ""))
      : String(vb || "").localeCompare(String(va || ""));
  });

  const shown = showAll ? sorted : sorted.slice(0, 10);

  const onSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

 const getSignalSLValue = (item, field) => {
  const v = norm(item[field]);

  if (!v) return '';

  if (v.includes('SIGNAL_CLOSE')) return '';

  if (v.includes('BULL')) return item.bullishSignalSL || '';

  if (v.includes('BEAR')) return item.bearishSignalSL || '';

  return '';
};

  return (
    <div className="tp-section">
      <h3>{tpTitles(field)}</h3>

      <div className="tp-filters">
        <select
          value={filters.symbol}
          onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
        >
          <option value="">Symbol</option>
          {symbols.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={filters.timeFrame}
          onChange={(e) => setFilters({ ...filters, timeFrame: e.target.value })}
        >
          <option value="">Time Frame</option>
          {timeFrames.map((t) => (
            <option key={t} value={t}>
              {tfLabel(t)}
            </option>
          ))}
        </select>

        <select
          value={filters.signalType}
          onChange={(e) => setFilters({ ...filters, signalType: e.target.value })}
        >
          <option value="">Signal Type</option>
          {uniqueSignals.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {filtered.length > 10 && (
          <button
            className="expand-button"
            onClick={() => setShowAll((s) => !s)}
          >
            {showAll ? "Collapse" : "Expand"}
          </button>
        )}

        <button
          onClick={() =>
            setFilters({ symbol: "", timeFrame: "", signalType: "" })
          }
        >
          Clear Filters
        </button>
      </div>

      <div className="tp-table-wrapper">
        <table className="tp-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => onSort("symbol")}>Symbol</th>
              <th>Chart</th>
              <th onClick={() => onSort("timeFrame")}>Time Frame</th>
              <th onClick={() => onSort("calculatedCandleCloseTime")}>
                Candle Close Time
              </th>
              <th onClick={() => onSort("marketPrice")}>Market Price</th>
              <th onClick={() => onSort("change")}>Change %</th>
              <th onClick={() => onSort('volumeRank')}>VolumeRank</th>
              <th>Signal SL</th>              
              <th onClick={() => onSort(field)}>{tpTitles(field)}</th>

            </tr>
          </thead>
          <tbody>
            {shown.map((row, idx) => {
              const v = String(row[field] || "");
              const isBull = v.toUpperCase().includes("BULL");
              const isBear = v.toUpperCase().includes("BEAR");
              const cls = isBull ? "bull" : isBear ? "bear" : "";
              return (
                <tr key={idx} className={cls}>
                  <td>{idx + 1}</td>
                  <td>{row.symbol}</td>
                <td>
                  <TradingViewChartIcon
                    symbol={row.symbol}
                    timeFrame={row.timeFrame}
                    exchange={row.exchange}
                  />
                </td>                  
                  <td>{tfLabel(row.timeFrame)}</td>
                  <td>{row.calculatedCandleCloseTime || "-"}</td>
                  <td>{row.marketPrice}</td>
                  <td>{row.change}</td>    
                  <td>{row.volumeRank}</td>   
                  <td>{getSignalSLValue(row, field)}</td>                                  
                  <td>{row[field]}</td>

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


